import { useQuery, type QueryClient, type QueryObserverResult, type RefetchOptions } from '@tanstack/react-query';
import {
  assignAndGetAllQuestsUserQuestsAssignAndGetGet,
} from '../../../client';
import type {
  DailyQuestWithProgress,
  DailyQuestAssignAndGetResponse,
  AllQuestsAssignAndGetResponse,
} from '../../../models/apiTypes';
import { preserveOptimisticQuestState } from '../../../utils/quest/optimisticQuestState';

export interface UseDailyQuestsRefetchParams {
  queryClient: QueryClient;
  isOpen: boolean;
  hourlyQuestsData: DailyQuestAssignAndGetResponse | undefined;
  marketingQuestsData: DailyQuestAssignAndGetResponse | undefined;
  dailyQuestsData: DailyQuestAssignAndGetResponse | undefined;
  mainQuestsData: DailyQuestAssignAndGetResponse | undefined;
  repeatableQuestsData: DailyQuestAssignAndGetResponse | undefined;
  optimisticallyCompletedAdQuestsRef: { current: Set<number> };
  optimisticallyClaimedRewardsRef: { current: Set<number> };
}

export interface UseDailyQuestsRefetchResult {
  refetchAllQuests: (options?: RefetchOptions) => Promise<QueryObserverResult<AllQuestsAssignAndGetResponse, Error>>;
}

export function useDailyQuestsRefetch({
  queryClient,
  isOpen,
  hourlyQuestsData,
  marketingQuestsData,
  dailyQuestsData,
  mainQuestsData,
  repeatableQuestsData,
  optimisticallyCompletedAdQuestsRef,
  optimisticallyClaimedRewardsRef,
}: UseDailyQuestsRefetchParams): UseDailyQuestsRefetchResult {
  // Отримуємо всі квести одним запитом
  const { refetch: refetchAllQuests } = useQuery<AllQuestsAssignAndGetResponse>({
    queryKey: ['allQuests'],
    queryFn: async () => {
      const result = await (async () => {
        const { data } = await assignAndGetAllQuestsUserQuestsAssignAndGetGet({ throwOnError: true });
        return data;
      })();
      
      // Зберігаємо оптимістично завершені ad quests та оптимістично забрані нагороди перед оновленням даних
      const protectOptimisticQuests = (
        quests: DailyQuestWithProgress[], 
        cacheKey: 'dailyQuests' | 'hourlyQuests' | 'marketingQuests'
      ): DailyQuestWithProgress[] => {
        const currentData = queryClient.getQueryData<DailyQuestAssignAndGetResponse>([cacheKey]);
        
        return quests.map((quest: DailyQuestWithProgress) => {
          let protectedQuest = quest;
          
          // Якщо цей квест був оптимістично завершений, зберігаємо його оптимістичний стан
          // quest тут з API (DailyQuestWithProgress), тому використовуємо user_quest_id
          if (quest.user_quest_id && optimisticallyCompletedAdQuestsRef.current.has(quest.user_quest_id)) {
            const optimisticQuest = currentData?.quests?.find((q) => q.user_quest_id === quest.user_quest_id);
            if (optimisticQuest) {
              // Перевіряємо, чи оптимістичний квест має завершений ad objective
              const adObjective = optimisticQuest.objectives?.find(
                (obj) => (obj.type === 'watch_ad' || obj.type === 'watch_ads') && obj.is_completed
              );
              if (adObjective && adObjective.current_count >= adObjective.target_count) {
                // Зберігаємо оптимістичний стан
                protectedQuest = optimisticQuest;
              }
            }
          }
          
          // Якщо нагорода була оптимістично забрана, зберігаємо reward_claimed: true
          // protectedQuest тут з API (DailyQuestWithProgress), тому використовуємо user_quest_id
          if (protectedQuest.user_quest_id && optimisticallyClaimedRewardsRef.current.has(protectedQuest.user_quest_id)) {
            protectedQuest = {
              ...protectedQuest,
              reward_claimed: true,
            };
          }
          
          return protectedQuest;
        });
      };
      // Both cache writers must agree, so run the shared module-level guard on
      // top of the ref-based one above (prefetchAllQuests applies the same).
      const protect = (
        quests: DailyQuestWithProgress[],
        cacheKey: 'dailyQuests' | 'hourlyQuests' | 'marketingQuests',
      ): DailyQuestWithProgress[] => preserveOptimisticQuestState(protectOptimisticQuests(quests, cacheKey));
      
      // Оновлюємо дані в кеші для кожного типу квестів окремо (для сумісності)
      // Зберігаємо оптимістично завершені ad quests
      queryClient.setQueryData(['dailyQuests'], {
        message: result.message,
        quests: protect(result.daily, 'dailyQuests'),
      });
      queryClient.setQueryData(['hourlyQuests'], {
        message: result.message,
        quests: protect(result.hourly, 'hourlyQuests'),
      });
      queryClient.setQueryData(['marketingQuests'], {
        message: result.message,
        quests: protect(result.marketing, 'marketingQuests'),
      });
      queryClient.setQueryData(['repeatableQuests'], {
        message: result.message,
        quests: preserveOptimisticQuestState(result.repeatable),
      });
      return result;
    },
    enabled: isOpen,
    // Ініціалізуємо з даних кешу, якщо вони є
    initialData: hourlyQuestsData && marketingQuestsData && dailyQuestsData
      ? {
          message: dailyQuestsData.message,
          daily: dailyQuestsData.quests,
          hourly: hourlyQuestsData.quests,
          marketing: marketingQuestsData.quests,
          main: mainQuestsData?.quests || [],
          repeatable: repeatableQuestsData?.quests || [],
        }
      : undefined,
    retry: false,
    refetchInterval: false,
    staleTime: 0,
    gcTime: Infinity, // Зберігаємо квести в кеші назавжди, щоб вони не зникали після закриття модалки
    refetchOnMount: 'always',
    refetchOnReconnect: true,
    refetchOnWindowFocus: false,
  });

  return {
    refetchAllQuests,
  };
}
